import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { GuideSummary, Hub, PostSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight, BookOpen, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { JsonLd } from '@/components/generals/json-ld'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PostCard } from '@/components/blog/post-card'
import { Pagination } from '@/components/blog/pagination'

import { getHubTheme, HUBS } from '@/constants/hubs'
import { buildGraph, breadcrumbNode, collectionNode } from '@/lib/structured-data'
import { getHub, getPostsByHub } from '@/lib/posts'
import { getGuideByHub } from '@/lib/guides'

const PER_PAGE = 9

interface HubPageProps {
    hub: Hub
    posts: PostSummary[]
    guide: GuideSummary | null
    page: number
    totalPages: number
    total: number
}

const HubPage: FC<HubPageProps> = ({ hub, posts, guide, page, totalPages, total }): ReactNode => {
    const theme = getHubTheme(hub.slug)
    const Icon = theme.icon
    const others = HUBS.filter(item => item.slug !== hub.slug)

    return (
        <Layout>
            <Seo title={hub.name} description={hub.description} canonical={`/${hub.slug}`} />
            <JsonLd
                data={buildGraph([
                    breadcrumbNode([
                        { name: 'Home', path: '/' },
                        { name: hub.name, path: `/${hub.slug}` }
                    ]),
                    collectionNode(hub, posts)
                ])}
            />

            <section className={cn('border-b border-border', theme.softBg)}>
                <Container className='py-12 sm:py-16'>
                    <nav aria-label='Breadcrumb' className='flex items-center gap-1.5 text-sm text-muted-foreground'>
                        <Link href='/' className='transition-colors hover:text-foreground'>
                            Home
                        </Link>
                        <ChevronRight className='size-3.5' />
                        <span className='font-medium text-foreground' data-mdlfy-cms={`hubs:${hub.slug}:name`}>
                            {hub.name}
                        </span>
                    </nav>

                    <div className='mt-7 flex items-center gap-4'>
                        <div className={cn('flex size-14 items-center justify-center rounded-2xl text-white', theme.solidBg)}>
                            <Icon className='size-7' />
                        </div>
                        <p
                            className={cn('text-base font-semibold', theme.ink)}
                            data-mdlfy-cms={`hubs:${hub.slug}:tagline`}
                        >
                            {hub.tagline}
                        </p>
                    </div>

                    <h1
                        className='mt-5 text-4xl font-bold tracking-tight sm:text-5xl'
                        data-mdlfy-cms={`hubs:${hub.slug}:name`}
                    >
                        {hub.name}
                    </h1>

                    <p
                        className='mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground'
                        data-mdlfy-cms={`hubs:${hub.slug}:description`}
                    >
                        {hub.description}
                    </p>

                    <p className='mt-6 text-sm font-medium text-muted-foreground'>
                        <span>{total}</span> {total === 1 ? 'article' : 'articles'}
                    </p>
                </Container>
            </section>

            {guide && (
                <section className='border-b border-border bg-card'>
                    <Container className='py-6'>
                        <Link
                            href={`/guides/${guide.slug}`}
                            className='group flex flex-col gap-3 rounded-xl border border-border p-5 transition-colors hover:bg-secondary/50 sm:flex-row sm:items-center sm:gap-5'
                        >
                            <div className={cn('flex size-11 shrink-0 items-center justify-center rounded-xl', theme.softBg)}>
                                <BookOpen className={cn('size-5', theme.ink)} aria-hidden='true' />
                            </div>

                            <div className='min-w-0 flex-1'>
                                <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                                    New here? Start with the complete guide
                                </p>
                                <p
                                    className='mt-1 font-heading font-semibold tracking-tight group-hover:text-primary'
                                    data-mdlfy-cms={`guides:${guide.id}:title`}
                                >
                                    {guide.title}
                                </p>
                                <p
                                    className='mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground'
                                    data-mdlfy-cms={`guides:${guide.id}:standfirst`}
                                >
                                    {guide.standfirst}
                                </p>
                            </div>

                            <ArrowRight
                                className='hidden size-5 shrink-0 text-muted-foreground group-hover:text-primary sm:block'
                                aria-hidden='true'
                            />
                        </Link>
                    </Container>
                </section>
            )}

            <section className='py-12 sm:py-16'>
                <Container>
                    {posts.length === 0 ? (
                        <div className='rounded-2xl border border-dashed border-border bg-card py-16 text-center'>
                            <p className='font-heading text-lg font-semibold'>No articles here yet</p>
                            <p className='mx-auto mt-2 max-w-sm text-sm text-muted-foreground'>
                                New {hub.name} guidance is on the way. Check back soon, or explore another topic.
                            </p>
                            <Link
                                href='/'
                                className='mt-6 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90'
                            >
                                Back to home
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                                {posts.map(post => (
                                    <PostCard key={post.slug} post={post} />
                                ))}
                            </div>

                            <div className='mt-12'>
                                <Pagination basePath={`/${hub.slug}`} page={page} totalPages={totalPages} />
                            </div>
                        </>
                    )}
                </Container>
            </section>

            <section className='border-t border-border bg-secondary/40 py-14'>
                <Container>
                    <h2 className='text-xl font-bold tracking-tight'>Explore other topics</h2>

                    <div className='mt-6 grid gap-4 sm:grid-cols-2'>
                        {others.map(item => {
                            const otherTheme = getHubTheme(item.slug)
                            const OtherIcon = otherTheme.icon

                            return (
                                <Link
                                    key={item.slug}
                                    href={`/${item.slug}`}
                                    className='group flex items-center gap-4 rounded-xl border border-border bg-card p-5 transition-colors hover:bg-secondary'
                                >
                                    <div className={cn('flex size-11 items-center justify-center rounded-xl', otherTheme.softBg)}>
                                        <OtherIcon className={cn('size-5', otherTheme.ink)} />
                                    </div>
                                    <div>
                                        <p className='font-heading font-semibold tracking-tight'>{item.name}</p>
                                        <p className='text-sm text-muted-foreground'>{item.tagline}</p>
                                    </div>
                                </Link>
                            )
                        })}
                    </div>
                </Container>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HubPageProps> = async ({ params, query }) => {
    const slug = params?.hub

    if (typeof slug !== 'string') return { notFound: true }

    const hub = await getHub(slug)

    if (!hub) return { notFound: true }

    const pageParam = Array.isArray(query.page) ? query.page[0] : query.page
    const parsed = parseInt(pageParam ?? '1', 10)
    const page = Number.isFinite(parsed) && parsed > 0 ? parsed : 1

    const [{ posts, total }, guide] = await Promise.all([
        getPostsByHub(slug, page, PER_PAGE),
        getGuideByHub(slug)
    ])
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE))

    return { props: { hub, posts, guide, page, totalPages, total } }
}

export default HubPage
